// src/pages/portal/Dashboard.tsx
//
// Customer portal dashboard. Data comes from the REAL backend:
// /api/portal/orders (server-scoped to the caller's own customer via
// kc sub). Never from demoData — a demo order can never appear as a
// customer's real order.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Package, Receipt, Tag, Euro } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { usePortal } from "@/lib/portal/PortalContext";
import { useAuth } from "@/components/auth/AuthContext";
import { authHeaders } from "@/lib/api";
import { formatPrice } from "@/lib/formatPrice";
import { FilemakerSyncCard } from "./FilemakerSyncCard";

interface PortalOrderLine {
  seq: number;
  label: string | null;
  quantity: number;
  line_total_tvac: string | number | null;
}

interface PortalOrder {
  order_id: string;
  status: string;
  created_at: string | null;
  total_tvac: string | number | null;
  lines: PortalOrderLine[];
  fulfillment: { status: string; simulated: boolean } | null;
}

const OPEN_STATUSES = new Set(["new", "confirmed"]);
const OPEN_FULFILLMENT = new Set(["pending", "picking", "packed",
  "partially_shipped", "shipped"]);

export function PortalDashboard() {
  const { t } = useTranslation();
  const { customer } = usePortal();
  const auth = useAuth();
  const [orders, setOrders] = useState<PortalOrder[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!auth.isReady || !auth.isAuthenticated) return;
    setError("");
    fetch("/api/portal/orders", { headers: authHeaders() })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = (await r.json()) as { rows: PortalOrder[] };
        setOrders(body.rows);
      })
      .catch((e) => setError(String(e instanceof Error ? e.message : e)));
  }, [auth.isReady, auth.isAuthenticated]);

  if (!customer) return null;
  const myOrders = orders ?? [];
  const open = myOrders.filter(
    (o) => OPEN_STATUSES.has(o.status)
      || (o.fulfillment && OPEN_FULFILLMENT.has(o.fulfillment.status)),
  );
  const shopSpend = myOrders.reduce((s, o) => s + Number(o.total_tvac ?? 0), 0);
  const recent = myOrders.slice(0, 4);

  const kpis = [
    { label: t("portal.dashboard.openOrders"), value: open.length, icon: Package, accent: "indigo" as const },
    { label: t("portal.dashboard.shopSpend"), value: formatPrice(shopSpend, "EUR"), icon: Euro, accent: "green" as const },
    { label: t("portal.dashboard.totalOrders"), value: myOrders.length, icon: Receipt, accent: "teal" as const },
    { label: t("portal.dashboard.pricingTier"), value: customer.pricingTier.toUpperCase(), icon: Tag, accent: "amber" as const },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi) => (
          <Card key={kpi.label}>
            <CardContent className="space-y-2 p-5">
              <div className="text-3xl font-510 tracking-tighter text-fg-primary">
                {kpi.value}
              </div>
              <div className="text-xs text-fg-tertiary">{kpi.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* BENCHMARK ↔ FileMaker status */}
      <div className="grid gap-3 lg:grid-cols-[1fr_320px]">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle>{t("portal.dashboard.recentOrders")}</CardTitle>
              <CardDescription>
                {t("portal.dashboard.recentOrdersDescription")}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm">
              <Link to="/portal/orders">{t("portal.dashboard.viewAll")}</Link>
            </Button>
          </CardHeader>
          <CardContent className="divide-y divide-border p-0">
            {error && (
              <div className="p-6 text-sm text-fg-tertiary">
                {t("portal.dashboard.loadError")} ({error})
              </div>
            )}
            {!error && orders === null && (
              <div className="p-6 text-sm text-fg-tertiary">{t("portal.dashboard.loading")}</div>
            )}
            {!error && orders !== null && recent.length === 0 && (
              <div className="p-6 text-sm text-fg-tertiary">{t("portal.dashboard.emptyOrders")}</div>
            )}
            {recent.map((o) => (
              <Link
                key={o.order_id}
                to="/portal/orders"
                className="flex flex-wrap items-center gap-3 px-5 py-3 transition-colors hover:bg-bg-elevated"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-510 text-fg-primary">
                    <code className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono text-[10px]">{o.order_id}</code>
                    <Badge
                      variant={
                        o.fulfillment?.status === "delivered" ? "success"
                          : o.fulfillment ? "teal"
                          : o.status === "cancelled" ? "danger"
                          : "default"
                      }
                      size="sm"
                    >
                      {o.fulfillment?.status ?? o.status}
                    </Badge>
                    {o.fulfillment?.simulated && (
                      <Badge variant="warning" size="sm">{t("portal.dashboard.demoShipping")}</Badge>
                    )}
                    <span className="text-[10px] text-fg-quaternary">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-fg-tertiary">
                    {o.lines.length} {o.lines.length === 1 ? t("portal.dashboard.item") : t("portal.dashboard.items")} -{" "}
                    {o.lines.reduce((s, l) => s + Number(l.quantity), 0)} {t("portal.dashboard.units")}
                  </div>
                </div>
                <div className="font-mono text-sm font-510 text-fg-primary">
                  {formatPrice(Number(o.total_tvac ?? 0), "EUR")}
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>
        <FilemakerSyncCard />
      </div>
    </div>
  );
}
